import React, { useRef, useCallback, useEffect } from 'react';
import { API_BASE_URL } from '../../services/api';
import {
  TextWithParagraphs,
  SectionHeadingH1,
  SectionHeadingH2,
  ChartCaption,
} from './ReportComponents';
import InlineTextEditor from './InlineTextEditor';
import ManagedDisclaimerBlock from '../../components/ManagedDisclaimerBlock';
import { fyFooterWithPartial } from '../../utils/performanceDisclosure';
import { resolveColours } from './colourPalette';
import PerformanceChart from './charts/PerformanceChart';
import AllocationVsSaaChart from './charts/AllocationVsSaaChart';
import ActiveTiltsChart from './charts/ActiveTiltsChart';
import AllocationDonutChart from './charts/AllocationDonutChart';
import HistoricalAllocationChart from './charts/HistoricalAllocationChart';
import CreditExposureBarChart from './charts/CreditExposureBarChart';
import WaterfallChart from './charts/WaterfallChart';
import SectorExposureChart from './charts/SectorExposureChart';
import RiskReturnScatterChart from './charts/RiskReturnScatterChart';
import DrawdownChart from './charts/DrawdownChart';
import GrowthDefensiveChart from './charts/GrowthDefensiveChart';
import RegionalLookthroughChart from './charts/RegionalLookthroughChart';
import SectorLookthroughChart from './charts/SectorLookthroughChart';
import AllocationBarChart from '../FactSheetApp/charts/AllocationDonutChart';
import FSCreditExposureChart from '../FactSheetApp/charts/CreditExposureChart';
import FSPerformanceChart from '../FactSheetApp/charts/PerformanceChart';

const formatPercent = (val) => {
  if (val === null || val === undefined) return '\u2014';
  return `${Number(val).toFixed(1)}%`;
};

const SECURITY_ABBR_KEEP = new Set([
  'BHP', 'ANZ', 'CSL', 'NAB', 'CBA', 'ASX', 'AMP', 'QBE', 'REA', 'TLS',
  'WBC', 'RIO', 'LTD', 'PTY', 'PLC', 'ORD', 'REIT', 'ETF',
]);

const titleCaseSecurity = (s) => {
  if (!s) return s;
  return s.split(/(\s+)/).map(tok => {
    if (/^\s+$/.test(tok) || tok.length === 0) return tok;
    const upper = tok.toUpperCase();
    if (SECURITY_ABBR_KEEP.has(upper)) return upper;
    return upper.charAt(0) + tok.slice(1).toLowerCase();
  }).join('');
};

/**
 * Returns table header colours from the resolved colour palette.
 */
const getTableColours = (data) => {
  const colours = resolveColours(data);
  return {
    headerPrimary: colours.table_header_primary,
    headerSecondary: colours.table_header_secondary,
  };
};

/** Returns inline style objects for table headers — always applied to guarantee
 *  correct colours regardless of CSS load order from other app stylesheets. */
const brandedHeaderStyle = (data, level) => {
  const { headerPrimary, headerSecondary } = getTableColours(data);
  if (level === 'primary') return { background: headerPrimary, color: '#ffffff' };
  return { background: headerSecondary, color: headerPrimary };
};

/**
 * Renders text as either a static TextWithParagraphs or an inline TipTap editor.
 * Used by all text block components to support inline editing.
 */
const EditableText = ({ text, editProps, contentKey, blockId, className = 'section-text' }) => {
  if (editProps?.editMode && contentKey) {
    return (
      <InlineTextEditor
        value={text}
        onChange={(html) => editProps.onTextChange(contentKey, html)}
        editMode={true}
        blockId={blockId || contentKey}
        onFocus={editProps.onTextFocus}
        onBlur={editProps.onTextBlur}
        className={className}
      />
    );
  }
  if (editProps?.editMode && editProps?.customBlockId) {
    return (
      <InlineTextEditor
        value={text}
        onChange={(html) => editProps.onCustomTextChange(editProps.customBlockId, html)}
        editMode={true}
        blockId={editProps.customBlockId}
        onFocus={editProps.onTextFocus}
        onBlur={editProps.onTextBlur}
        className={className}
      />
    );
  }
  return <TextWithParagraphs text={text} className={className} />;
};

/**
 * Editable heading — uses InlineTextEditor (TipTap) for full formatting support
 * (bold, italic, colour via the floating toolbar) plus a rule toggle button.
 */
const EditableHeading = ({ blockId, defaultText, data, editProps, HeadingComponent, headingKey }) => {
  const key = headingKey || blockId;

  // Chart and table titles (no HeadingComponent supplied) are fixed —
  // not editable, never overridden by saved content. Render the static default.
  if (!HeadingComponent) {
    return <h2 className="section-heading">{defaultText}</h2>;
  }

  const override = data.content?.heading_overrides?.[key];
  const text = override !== undefined && override !== '' ? override : defaultText;
  const styles = data.content?.heading_styles?.[key] || {};

  // Determine heading class and default rule visibility
  const headingClass = HeadingComponent === SectionHeadingH1 ? 'qr-h1'
    : HeadingComponent === SectionHeadingH2 ? 'qr-h2'
    : 'section-heading';
  const defaultShowRule = HeadingComponent === SectionHeadingH1; // H1 has rule by default
  const showRule = styles.showRule !== undefined ? styles.showRule : defaultShowRule;

  if (editProps?.editMode) {
    return (
      <div className={`editable-heading-wrap ${headingClass} ${showRule ? 'has-rule' : 'no-rule'}`}>
        <InlineTextEditor
          value={text}
          onChange={(html) => editProps.onHeadingChange?.(key, html)}
          editMode={true}
          blockId={`heading_${key}`}
          onFocus={editProps.onTextFocus}
          onBlur={editProps.onTextBlur}
          className={`heading-editor ${headingClass}`}
        />
        <button
          className={`heading-rule-toggle ${showRule ? 'active' : ''}`}
          onClick={() => editProps.onHeadingStyleChange?.(key, { showRule: !showRule })}
          title={showRule ? 'Remove line below' : 'Add line below'}
          onMouseDown={(e) => e.preventDefault()}
        >
          ―
        </button>
      </div>
    );
  }

  // View mode — render heading with optional rule
  const content = /<[a-z][\s\S]*>/i.test(text)
    ? <span dangerouslySetInnerHTML={{ __html: text }} />
    : text;

  if (HeadingComponent && showRule && !defaultShowRule) {
    // Non-default rule: render with custom rule class
    const Tag = HeadingComponent === SectionHeadingH1 ? 'h2' : HeadingComponent === SectionHeadingH2 ? 'h3' : 'h2';
    return <Tag className={`${headingClass} has-rule`}>{content}</Tag>;
  }
  if (HeadingComponent && !showRule && defaultShowRule) {
    // Default rule removed
    const Tag = HeadingComponent === SectionHeadingH1 ? 'h2' : 'h3';
    return <Tag className={`${headingClass} no-rule`}>{content}</Tag>;
  }
  if (HeadingComponent) {
    return <HeadingComponent>{content}</HeadingComponent>;
  }
  return <h2 className={`section-heading ${showRule ? 'has-rule' : ''}`}>{content}</h2>;
};

// ── Block Registry ──────────────────────────────────────────────
export const BLOCK_REGISTRY = {
  title_box:                     { type: 'special', label: 'Title Box' },
  intro_text:                    { type: 'text',    label: 'Introduction',           contentKey: 'intro_text' },
  market_overview:               { type: 'text',    label: 'Market Overview',        contentKey: 'market_overview' },
  performance_review:            { type: 'text',    label: 'Performance & Review',   contentKey: 'performance_review' },
  perf_table:                    { type: 'data',    label: 'Performance & Risk Table' },
  fy_table:                      { type: 'data',    label: 'FY Returns Table' },
  holdings_and_characteristics:  { type: 'data',    label: 'Holdings & Characteristics' },
  charts_tilts_and_allocation:   { type: 'data',    label: 'Active Tilts & Allocation' },
  allocation_donut_chart:        { type: 'data',    label: 'Allocation Donut' },
  historical_allocation_chart:   { type: 'data',    label: 'Historical Allocation' },
  corporate_debt_review:         { type: 'text',    label: 'Corporate Debt',         contentKey: 'corporate_debt_review' },
  credit_exposure_chart:         { type: 'data',    label: 'Credit Exposure Chart' },
  government_bonds_review:       { type: 'text',    label: 'Government Bonds',       contentKey: 'government_bonds_review' },
  international_equities_review: { type: 'text',    label: 'International Equities', contentKey: 'international_equities_review' },
  australian_equities_review:    { type: 'text',    label: 'Australian Equities',    contentKey: 'australian_equities_review' },
  real_assets_review:            { type: 'text',    label: 'Real Assets',            contentKey: 'real_assets_review' },
  global_alpha_review:           { type: 'text',    label: 'Global Alpha',           contentKey: 'global_alpha_review' },
  outlook:                       { type: 'text',    label: 'Outlook',                contentKey: 'outlook' },
  sign_off:                      { type: 'special', label: 'Sign Off' },
};

export const DEFAULT_LAYOUT = [
  { page: 1, blocks: [{ id: 'title_box', x: 0, y: 0, w: 12, h: 6 }] },
];

// ── Sub-components ──────────────────────────────────────────────

const TitleBox = ({ data }) => {
  const titleOverrides = data.content?.title_box || {};
  const colours = resolveColours(data);
  const titleText = data.branding
    ? data.portfolio_name
    : (titleOverrides.title_text || 'Drummond Strategic Series');
  const titleCode = titleOverrides.title_code || data.portfolio_code || '';
  const rhsMode = data.branding ? 'logo' : 'code';
  const isMonthly = data.quarter && !data.quarter.includes('-Q');
  const defaultSubtitle = `${isMonthly ? 'Monthly Update' : 'Quarterly Review'} \u2013 ${data.quarter_display}`;
  const subtitle = titleOverrides.subtitle || defaultSubtitle;
  const accentColour = titleOverrides.accent_colour || colours.title_accent;

  // Auto-shrink title font if it wraps to multiple lines
  const h1Ref = useRef(null);
  useEffect(() => {
    const el = h1Ref.current;
    if (!el) return;
    // Reset to default size
    el.style.fontSize = '';
    const maxSize = 44; // 33pt in px
    const minSize = 24;
    let size = maxSize;
    // Measure single-line height by temporarily preventing wrap
    el.style.whiteSpace = 'nowrap';
    const singleLineHeight = el.offsetHeight;
    el.style.whiteSpace = '';
    // Shrink until content fits in one line
    while (size > minSize) {
      el.style.fontSize = `${size}px`;
      if (el.offsetHeight <= singleLineHeight + 2) break;
      size -= 1;
    }
  }, [titleText]);

  return (
    <div className="qr-title-box">
      <div className="qr-title-row">
        <h1 ref={h1Ref}>{titleText}</h1>
        {rhsMode === 'code' && titleCode && (
          <div className="qr-title-rhs">
            <span className="qr-title-code" style={{ color: accentColour }}>{titleCode}</span>
          </div>
        )}
        {rhsMode === 'logo' && (
          <div className="qr-title-rhs">
            <img
              src={data.branding?.logo_url
                ? (data.branding.logo_url.startsWith('/api') ? `${API_BASE_URL}${data.branding.logo_url}` : data.branding.logo_url)
                : '/logo-name.png'}
              alt="Logo"
              className="qr-title-logo"
              data-original-src={data.branding?.logo_url
                ? (data.branding.logo_url.startsWith('/api') ? `${API_BASE_URL}${data.branding.logo_url}` : data.branding.logo_url)
                : ''}
              onError={(e) => { e.target.src = '/logo-name.png'; }}
            />
          </div>
        )}
      </div>
      <p style={{ color: colours.subtitle_colour }}>{subtitle}</p>
    </div>
  );
};

const IntroTextBlock = ({ data, editProps }) => {
  if (!editProps?.editMode && !data.content?.intro_text) return null;
  return (
    <section style={{ marginBottom: '4mm' }}>
      <EditableText text={data.content?.intro_text} editProps={editProps} contentKey="intro_text" className="intro-text" />
    </section>
  );
};

const MarketOverviewBlock = ({ data, editProps }) => {
  if (!editProps?.editMode && !data.content?.market_overview) return null;
  return (
    <>
      <EditableHeading blockId="market_overview" defaultText="Market Overview" data={data} editProps={editProps} HeadingComponent={SectionHeadingH1} />
      <div className="qr-body">
        <EditableText text={data.content?.market_overview} editProps={editProps} contentKey="market_overview" />
      </div>
    </>
  );
};

const PerformanceReviewBlock = ({ data, editProps }) => {
  if (!editProps?.editMode && !data.content?.performance_review) return null;
  return (
    <>
      <EditableHeading blockId="performance_review" defaultText="Portfolio Performance & Review" data={data} editProps={editProps} HeadingComponent={SectionHeadingH1} />
      <div className="qr-body">
        <EditableText text={data.content?.performance_review} editProps={editProps} contentKey="performance_review" />
      </div>
    </>
  );
};

const PerfTableBlock = ({ data, editProps }) => {
  const perf = data.performance || {};
  const vol = data.volatility_data || {};
  const timeSeries = data.time_series;

  const perfColumns = [
    { label: '3m', portfolio: perf.return_3m, peer: perf.peer_3m },
    { label: '6m', portfolio: perf.return_6m, peer: perf.peer_6m },
    { label: '1yr.', portfolio: perf.return_1y, peer: perf.peer_1y },
    { label: '3yr', portfolio: perf.return_3y, peer: perf.peer_3y },
    { label: '5yr', portfolio: perf.return_5y, peer: perf.peer_5y },
    { label: 'S.I.(p.a.)', portfolio: perf.return_si, peer: perf.peer_si },
  ].filter(col => col.portfolio != null);

  const peerName = timeSeries?.benchmark_name || 'FE Growth Peer Index';

  // Risk columns. Each: lowerBetter=true means portfolio diff < 0 is the win
  // (vol & drawdown). Sharpe is higher-better.
  const riskColumns = [
    { label: 'Vol.(p.a.)', portfolio: vol.portfolio, peer: vol.peer,
      format: formatPercent, lowerBetter: true },
    { label: 'Return/Risk', portfolio: vol.portfolio_sharpe, peer: vol.peer_sharpe,
      format: v => Number(v).toFixed(2), lowerBetter: false },
    { label: 'Max DD',     portfolio: vol.portfolio_max_dd, peer: vol.peer_max_dd,
      format: formatPercent, lowerBetter: false },
  ].filter(col => col.portfolio != null || col.peer != null);

  if (perfColumns.length === 0 && riskColumns.length === 0) return null;

  const renderRiskCell = (col, side) => {
    const v = col[side];
    return v == null ? '\u2014' : col.format(v);
  };

  const secondaryStyle = brandedHeaderStyle(data, 'secondary');
  const primaryStyle = brandedHeaderStyle(data, 'primary');

  return (
    <>
      <EditableHeading blockId="perf_table" defaultText="PORTFOLIO PERFORMANCE & RISK" data={data} editProps={editProps} />
      <table className="performance-table-transposed">
        <thead style={secondaryStyle}>
          <tr>
            <th className="label-col">PERFORMANCE</th>
            {perfColumns.map((col, i) => (
              <th key={i}>{col.label}</th>
            ))}
            {riskColumns.map((col, i) => (
              <th key={`r${i}`} className="vol-header" style={secondaryStyle}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="label-col portfolio-name-cell">{data.portfolio_name}</td>
            {perfColumns.map((col, i) => (
              <td key={i}>{formatPercent(col.portfolio)}</td>
            ))}
            {riskColumns.map((col, i) => (
              <td key={`r${i}`}>{renderRiskCell(col, 'portfolio')}</td>
            ))}
          </tr>
          <tr>
            <td className="label-col">{peerName}</td>
            {perfColumns.map((col, i) => (
              <td key={i}>{formatPercent(col.peer)}</td>
            ))}
            {riskColumns.map((col, i) => (
              <td key={`r${i}`}>{renderRiskCell(col, 'peer')}</td>
            ))}
          </tr>
          <tr className="difference-row">
            <td className="label-col">Difference</td>
            {perfColumns.map((col, i) => {
              if (col.portfolio != null && col.peer != null) {
                const diff = col.portfolio - col.peer;
                return (
                  <td key={i} className={diff >= 0 ? 'positive-diff' : ''}>
                    {diff.toFixed(1)}%
                  </td>
                );
              }
              return <td key={i}>{'\u2014'}</td>;
            })}
            {riskColumns.map((col, i) => {
              if (col.portfolio == null || col.peer == null) {
                return <td key={`r${i}`}>{'\u2014'}</td>;
              }
              const diff = col.portfolio - col.peer;
              const isPositive = col.lowerBetter ? diff <= 0 : diff >= 0;
              const display = col.label === 'Return/Risk' ? diff.toFixed(2) : `${diff.toFixed(1)}%`;
              return (
                <td key={`r${i}`} className={isPositive ? 'positive-diff' : ''}>
                  {display}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </>
  );
};

// ── Monthly Performance & Risk table (includes 1m) ─────────────
const PerfTableMonthlyBlock = ({ data, editProps }) => {
  const perf = data.performance || {};
  const vol = data.volatility_data || {};
  const timeSeries = data.time_series;

  const perfColumns = [
    { label: '1m', portfolio: perf.return_1m, peer: perf.peer_1m },
    { label: '3m', portfolio: perf.return_3m, peer: perf.peer_3m },
    { label: '6m', portfolio: perf.return_6m, peer: perf.peer_6m },
    { label: '1yr.', portfolio: perf.return_1y, peer: perf.peer_1y },
    { label: '3yr', portfolio: perf.return_3y, peer: perf.peer_3y },
    { label: '5yr', portfolio: perf.return_5y, peer: perf.peer_5y },
    { label: 'S.I.(p.a.)', portfolio: perf.return_si, peer: perf.peer_si },
  ].filter(col => col.portfolio != null);

  const peerName = timeSeries?.benchmark_name || 'FE Growth Peer Index';

  const riskColumns = [
    { label: 'Vol.(p.a.)', portfolio: vol.portfolio, peer: vol.peer,
      format: formatPercent, lowerBetter: true },
    { label: 'Return/Risk', portfolio: vol.portfolio_sharpe, peer: vol.peer_sharpe,
      format: v => Number(v).toFixed(2), lowerBetter: false },
    { label: 'Max DD',     portfolio: vol.portfolio_max_dd, peer: vol.peer_max_dd,
      format: formatPercent, lowerBetter: false },
  ].filter(col => col.portfolio != null || col.peer != null);

  if (perfColumns.length === 0 && riskColumns.length === 0) return null;

  const renderRiskCell = (col, side) => {
    const v = col[side];
    return v == null ? '\u2014' : col.format(v);
  };

  const secondaryStyle = brandedHeaderStyle(data, 'secondary');

  return (
    <>
      <EditableHeading blockId="perf_table_monthly" defaultText="PORTFOLIO PERFORMANCE & RISK" data={data} editProps={editProps} />
      <table className="performance-table-transposed">
        <thead style={secondaryStyle}>
          <tr>
            <th className="label-col">PERFORMANCE</th>
            {perfColumns.map((col, i) => (
              <th key={i}>{col.label}</th>
            ))}
            {riskColumns.map((col, i) => (
              <th key={`r${i}`} className="vol-header" style={secondaryStyle}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="label-col portfolio-name-cell">{data.portfolio_name}</td>
            {perfColumns.map((col, i) => (
              <td key={i}>{formatPercent(col.portfolio)}</td>
            ))}
            {riskColumns.map((col, i) => (
              <td key={`r${i}`}>{renderRiskCell(col, 'portfolio')}</td>
            ))}
          </tr>
          <tr>
            <td className="label-col">{peerName}</td>
            {perfColumns.map((col, i) => (
              <td key={i}>{formatPercent(col.peer)}</td>
            ))}
            {riskColumns.map((col, i) => (
              <td key={`r${i}`}>{renderRiskCell(col, 'peer')}</td>
            ))}
          </tr>
          <tr className="difference-row">
            <td className="label-col">Difference</td>
            {perfColumns.map((col, i) => {
              if (col.portfolio != null && col.peer != null) {
                const diff = col.portfolio - col.peer;
                return (
                  <td key={i} className={diff >= 0 ? 'positive-diff' : ''}>
                    {diff.toFixed(1)}%
                  </td>
                );
              }
              return <td key={i}>{'\u2014'}</td>;
            })}
            {riskColumns.map((col, i) => {
              if (col.portfolio == null || col.peer == null) {
                return <td key={`r${i}`}>{'\u2014'}</td>;
              }
              const diff = col.portfolio - col.peer;
              const isPositive = col.lowerBetter ? diff <= 0 : diff >= 0;
              const display = col.label === 'Return/Risk' ? diff.toFixed(2) : `${diff.toFixed(1)}%`;
              return (
                <td key={`r${i}`} className={isPositive ? 'positive-diff' : ''}>
                  {display}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </>
  );
};

const FYTableBlock = ({ data, editProps }) => {
  const fyData = data.fy_returns || {};
  const fyColumns = fyData.columns || [];
  const timeSeries = data.time_series;
  const peerName = timeSeries?.benchmark_name || 'FE Growth Peer Index';

  if (fyColumns.length === 0) return null;

  const secondaryStyle = brandedHeaderStyle(data, 'secondary');

  // Footer matches the portfolio's fact sheet: resolved server-side (its own
  // footer, else Admin > Disclaimers), with the partial-year note when needed.
  const fyFooterText = fyFooterWithPartial(data.fy_table_footer, fyColumns.some(c => c.partial));

  return (
    <>
      <EditableHeading blockId="fy_table" defaultText="PERFORMANCE - FINANCIAL YEAR" data={data} editProps={editProps} />
      <table className="performance-table-transposed">
        <thead style={secondaryStyle}>
          <tr>
            <th className="label-col"></th>
            {fyColumns.map((col, i) => (
              <th key={i}>{col.partial ? `${col.label}*` : col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="label-col portfolio-name-cell">{data.portfolio_name}</td>
            {fyColumns.map((col, i) => (
              <td key={i}>{formatPercent(col.portfolio)}</td>
            ))}
          </tr>
          <tr>
            <td className="label-col">{peerName}</td>
            {fyColumns.map((col, i) => (
              <td key={i}>{formatPercent(col.peer)}</td>
            ))}
          </tr>
          <tr className="difference-row">
            <td className="label-col">Difference</td>
            {fyColumns.map((col, i) => {
              if (col.portfolio != null && col.peer != null) {
                const diff = col.portfolio - col.peer;
                return (
                  <td key={i} className={diff >= 0 ? 'positive-diff' : ''}>
                    {diff.toFixed(1)}%
                  </td>
                );
              }
              return <td key={i}>{'\u2014'}</td>;
            })}
          </tr>
        </tbody>
      </table>
      {data.fy_table_footer_show !== false && (
        <p className="perf-note">{fyFooterText}</p>
      )}
    </>
  );
};

const HoldingsBlock = ({ data, editProps }) => {
  const secondaryStyle = brandedHeaderStyle(data, 'secondary');
  return (
  <div className="two-column">
    <div className="left-column">
      <EditableHeading blockId="holdings_top5" defaultText="TOP 5 ACTIVE HOLDINGS" data={data} editProps={editProps} />
      {data.top_5_holdings && data.top_5_holdings.length > 0 ? (
        <table className="data-table">
          <thead style={secondaryStyle}>
            <tr>
              <th>Manager</th>
              <th className="center">Asset Class</th>
              <th className="center">Weight</th>
            </tr>
          </thead>
          <tbody>
            {data.top_5_holdings.map((h, i) => (
              <tr key={i}>
                <td>{h.name}</td>
                <td className="center">{h.asset_class}</td>
                <td className="center">{formatPercent(h.weight)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="section-text" style={{ color: '#999', fontStyle: 'italic' }}>No holdings data available</p>
      )}
    </div>
    <div className="right-column">
      <EditableHeading blockId="holdings_characteristics" defaultText="PORTFOLIO CHARACTERISTICS" data={data} editProps={editProps} />
      <table className="data-table metrics-table">
        <tbody>
          <tr><td>No. Holdings</td><td>{data.holdings_count || '\u2014'}</td></tr>
        </tbody>
      </table>
    </div>
  </div>
  );
};

const ChartsTiltsBlock = ({ data, chartRefs }) => {
  const hasChanges = data.portfolio_changes && data.portfolio_changes.length > 0;
  const tiltsData = hasChanges ? data.portfolio_changes : data.active_tilts;
  const caption = 'Portfolio Changes Over the Quarter';
  const colours = resolveColours(data);

  return (
    <div className="two-column">
      <div className="left-column">
        <ChartCaption>{caption}</ChartCaption>
        <ActiveTiltsChart data={tiltsData} colours={colours} ref={chartRefs.activeTiltsChartRef} />
      </div>
      <div className="right-column">
        <ChartCaption>Asset Allocation vs. SAA</ChartCaption>
        <AllocationVsSaaChart data={data.allocation_vs_saa} colours={colours} ref={chartRefs.allocationVsSaaChartRef} />
      </div>
    </div>
  );
};

const AllocationDonutBlock = ({ data, chartRefs }) => {
  const colours = resolveColours(data);
  return (
    <>
      <ChartCaption>Portfolio Asset Allocation</ChartCaption>
      <AllocationDonutChart allocation={data.allocation} colours={colours} portfolioCode={data.portfolio_code} ref={chartRefs.allocationDonutChartRef} />
    </>
  );
};

const HistoricalAllocationBlock = ({ data, chartRefs }) => {
  const colours = resolveColours(data);
  return (
    <>
      <ChartCaption>Historical Portfolio Asset Allocation</ChartCaption>
      <HistoricalAllocationChart data={data.historical_allocation} colours={colours} ref={chartRefs.historicalAllocationChartRef} />
    </>
  );
};

const CorporateDebtBlock = ({ data, editProps }) => {
  if (!editProps?.editMode && !data.content?.corporate_debt_review) return null;
  return (
    <div className="section-box">
      <EditableHeading blockId="corporate_debt_review" defaultText="Asset Class Review" data={data} editProps={editProps} HeadingComponent={SectionHeadingH2} headingKey="corporate_debt_category" />
      <EditableHeading blockId="corporate_debt_review" defaultText="Corporate Debt" data={data} editProps={editProps} HeadingComponent={SectionHeadingH2} />
      <div className="qr-body">
        <EditableText text={data.content?.corporate_debt_review} editProps={editProps} contentKey="corporate_debt_review" />
      </div>
    </div>
  );
};

const CreditExposureBlock = ({ data, chartRefs }) => {
  if (!data.credit_exposure || Object.keys(data.credit_exposure).length === 0) return null;
  const colours = resolveColours(data);
  return (
    <div className="section-box">
      <ChartCaption>
        Portfolio Look Through Credit Grade Exposure (Corporate Debt &amp; Government Bonds)
      </ChartCaption>
      <div className="chart-container credit-chart-container">
        <CreditExposureBarChart data={data.credit_exposure} colours={colours} ref={chartRefs.creditChartRef} />
      </div>
      <p className="perf-note">Source: Drummond Capital Partners, Fund Managers</p>
    </div>
  );
};

const GovernmentBondsBlock = ({ data, editProps }) => {
  if (!editProps?.editMode && !data.content?.government_bonds_review) return null;
  return (
    <div className="section-box">
      <EditableHeading blockId="government_bonds_review" defaultText="Government Bonds" data={data} editProps={editProps} HeadingComponent={SectionHeadingH2} />
      <div className="qr-body">
        <EditableText text={data.content?.government_bonds_review} editProps={editProps} contentKey="government_bonds_review" />
      </div>
    </div>
  );
};

const InternationalEquitiesBlock = ({ data, editProps }) => {
  if (!editProps?.editMode && !data.content?.international_equities_review) return null;
  return (
    <div className="section-box">
      <EditableHeading blockId="international_equities_review" defaultText="International Equities" data={data} editProps={editProps} HeadingComponent={SectionHeadingH2} />
      <div className="qr-body">
        <EditableText text={data.content?.international_equities_review} editProps={editProps} contentKey="international_equities_review" />
      </div>
    </div>
  );
};

const AustralianEquitiesBlock = ({ data, editProps }) => {
  if (!editProps?.editMode && !data.content?.australian_equities_review) return null;
  return (
    <div className="section-box">
      <EditableHeading blockId="australian_equities_review" defaultText="Australian Equities" data={data} editProps={editProps} HeadingComponent={SectionHeadingH2} />
      <div className="qr-body">
        <EditableText text={data.content?.australian_equities_review} editProps={editProps} contentKey="australian_equities_review" />
      </div>
    </div>
  );
};

const RealAssetsBlock = ({ data, editProps }) => {
  if (!editProps?.editMode && !data.content?.real_assets_review) return null;
  return (
    <div className="section-box">
      <EditableHeading blockId="real_assets_review" defaultText="Real Assets (Infrastructure & Real Estate)" data={data} editProps={editProps} HeadingComponent={SectionHeadingH2} />
      <div className="qr-body">
        <EditableText text={data.content?.real_assets_review} editProps={editProps} contentKey="real_assets_review" />
      </div>
    </div>
  );
};

const GlobalAlphaBlock = ({ data, editProps }) => {
  if (!editProps?.editMode && !data.content?.global_alpha_review) return null;
  return (
    <div className="section-box">
      <EditableHeading blockId="global_alpha_review" defaultText="Global Alpha" data={data} editProps={editProps} HeadingComponent={SectionHeadingH2} />
      <div className="qr-body">
        <EditableText text={data.content?.global_alpha_review} editProps={editProps} contentKey="global_alpha_review" />
      </div>
    </div>
  );
};

const OutlookBlock = ({ data, editProps }) => {
  if (!editProps?.editMode && !data.content?.outlook) return null;
  return (
    <>
      <EditableHeading blockId="outlook" defaultText="Outlook" data={data} editProps={editProps} HeadingComponent={SectionHeadingH1} />
      <div className="qr-body">
        <EditableText text={data.content?.outlook} editProps={editProps} contentKey="outlook" />
      </div>
    </>
  );
};

const SignOffBlock = () => (
  <div className="qr-sign-off">
    <p>Regards,</p>
    <p style={{ fontWeight: 600 }}>The Drummond Capital Partners team</p>
  </div>
);

// ── Block definition resolver ───────────────────────────────────
export const getBlockDef = (blockId, customBlocks) => {
  if (BLOCK_REGISTRY[blockId]) return BLOCK_REGISTRY[blockId];
  if (customBlocks && customBlocks[blockId]) return customBlocks[blockId];
  return { type: 'special', label: blockId };
};

// ── Dynamic custom text block ───────────────────────────────────
const DynamicTextBlock = ({ blockId, data, editProps }) => {
  const customBlocks = data.content?.custom_blocks || {};
  const customContent = data.content?.custom_content || {};
  const blockDef = customBlocks[blockId];
  if (!blockDef) return null;

  const html = customContent[blockId];
  const HeadingComponent = blockDef.headingLevel === 'h1' ? SectionHeadingH1
    : blockDef.headingLevel === 'h2' ? SectionHeadingH2
    : null;

  const customEditProps = editProps?.editMode
    ? { ...editProps, customBlockId: blockId }
    : editProps;

  const bgColor = blockDef.bgColor || null;
  const boxStyle = bgColor ? { background: bgColor, padding: '8px 10px', borderRadius: '3px', height: '100%', boxSizing: 'border-box' } : {};

  return (
    <div className="section-box" style={boxStyle}>
      {HeadingComponent && (blockDef.heading || editProps?.editMode) && (
        <EditableHeading blockId={blockId} defaultText={blockDef.heading || 'Heading'} data={data} editProps={editProps} HeadingComponent={HeadingComponent} />
      )}
      {(html || editProps?.editMode) && (
        <div className="qr-body">
          <EditableText text={html} editProps={customEditProps} />
        </div>
      )}
    </div>
  );
};

// ── Dynamic image block ─────────────────────────────────────────
const DynamicImageBlock = ({ blockId, data, editProps }) => {
  const customBlocks = data.content?.custom_blocks || {};
  const customContent = data.content?.custom_content || {};
  const blockDef = customBlocks[blockId];
  if (!blockDef) return null;

  const imageSrc = customContent[blockId];
  const objectFit = blockDef.objectFit || 'contain';
  const fileInputRef = useRef(null);

  const handleReplace = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (editProps?.onCustomTextChange) {
        editProps.onCustomTextChange(blockId, reader.result);
      }
    };
    reader.readAsDataURL(file);
  }, [blockId, editProps]);

  return (
    <div className="dynamic-image-block">
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={blockDef.label || 'Custom image'}
          className="dynamic-image-content"
          style={{ objectFit }}
        />
      ) : (
        <div className="dynamic-image-placeholder">
          No image uploaded
        </div>
      )}
      {editProps?.editMode && (
        <div className="dynamic-image-controls">
          <button
            className="image-replace-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            Replace Image
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleReplace}
          />
        </div>
      )}
    </div>
  );
};

// ── Dynamic shape block ─────────────────────────────────────────
const ShapeSvg = ({ shapeType, fillColor, strokeColor, strokeWidth }) => {
  const fill = fillColor || 'transparent';
  const stroke = strokeColor || '#072040';
  const sw = strokeWidth || 2;

  const svgProps = {
    width: '100%',
    height: '100%',
    viewBox: '0 0 100 100',
    preserveAspectRatio: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
  };

  switch (shapeType) {
    case 'rectangle':
      return (
        <svg {...svgProps}>
          <rect x={sw/2} y={sw/2} width={100 - sw} height={100 - sw}
            fill={fill} stroke={stroke} strokeWidth={sw} />
        </svg>
      );
    case 'rounded_rect':
      return (
        <svg {...svgProps}>
          <rect x={sw/2} y={sw/2} width={100 - sw} height={100 - sw} rx="8" ry="8"
            fill={fill} stroke={stroke} strokeWidth={sw} />
        </svg>
      );
    case 'circle':
      return (
        <svg {...svgProps} preserveAspectRatio="xMidYMid meet">
          <ellipse cx="50" cy="50" rx={50 - sw} ry={50 - sw}
            fill={fill} stroke={stroke} strokeWidth={sw} />
        </svg>
      );
    case 'line_h':
      return (
        <svg {...svgProps}>
          <line x1="0" y1="50" x2="100" y2="50"
            stroke={stroke} strokeWidth={sw} />
        </svg>
      );
    case 'line_v':
      return (
        <svg {...svgProps}>
          <line x1="50" y1="0" x2="50" y2="100"
            stroke={stroke} strokeWidth={sw} />
        </svg>
      );
    case 'arrow_right':
      return (
        <svg {...svgProps}>
          <line x1="5" y1="50" x2="85" y2="50"
            stroke={stroke} strokeWidth={sw} />
          <polyline points="75,35 90,50 75,65"
            fill="none" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
        </svg>
      );
    case 'arrow_down':
      return (
        <svg {...svgProps}>
          <line x1="50" y1="5" x2="50" y2="85"
            stroke={stroke} strokeWidth={sw} />
          <polyline points="35,75 50,90 65,75"
            fill="none" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
        </svg>
      );
    case 'triangle':
      return (
        <svg {...svgProps}>
          <polygon points={`50,${sw} ${100 - sw},${100 - sw} ${sw},${100 - sw}`}
            fill={fill} stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
        </svg>
      );
    default:
      return (
        <svg {...svgProps}>
          <rect x={sw/2} y={sw/2} width={100 - sw} height={100 - sw}
            fill={fill} stroke={stroke} strokeWidth={sw} />
        </svg>
      );
  }
};

const DynamicShapeBlock = ({ blockId, data, editProps }) => {
  const customBlocks = data.content?.custom_blocks || {};
  const blockDef = customBlocks[blockId];
  if (!blockDef) return null;

  const { shapeType, fillColor, strokeColor, strokeWidth, filled } = blockDef;
  const actualFill = filled ? (fillColor || '#072040') : 'transparent';

  return (
    <div className="dynamic-shape-block">
      <ShapeSvg
        shapeType={shapeType}
        fillColor={actualFill}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {editProps?.editMode && (
        <div className="shape-edit-label">{blockDef.label}</div>
      )}
    </div>
  );
};

// ── Dynamic template chart block ────────────────────────────────
const TemplateChartBlock = ({ blockId, data, chartRefs }) => {
  const customBlocks = data.content?.custom_blocks || {};
  const blockDef = customBlocks[blockId];
  if (!blockDef) return null;

  const { templateId } = blockDef;
  const dynRefs = chartRefs.dynamicChartRefs?.current || {};
  const colours = resolveColours(data);

  switch (templateId) {
    case 'perf_line_chart':
      return (
        <>
          <ChartCaption>Performance</ChartCaption>
          <PerformanceChart data={data.time_series} colours={colours} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'allocation_donut':
      return (
        <>
          <ChartCaption>Portfolio Asset Allocation</ChartCaption>
          <AllocationDonutChart allocation={data.allocation} colours={colours} portfolioCode={data.portfolio_code} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'allocation_vs_saa_bar':
      return (
        <>
          <ChartCaption>Asset Allocation vs. SAA</ChartCaption>
          <AllocationVsSaaChart data={data.allocation_vs_saa} colours={colours} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'active_tilts_bar': {
      const hasChanges = data.portfolio_changes && data.portfolio_changes.length > 0;
      const tiltsData = hasChanges ? data.portfolio_changes : data.active_tilts;
      const caption = 'Portfolio Changes Over the Quarter';
      return (
        <>
          <ChartCaption>{caption}</ChartCaption>
          <ActiveTiltsChart data={tiltsData} colours={colours} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    }
    case 'historical_allocation_area':
      return (
        <>
          <ChartCaption>Historical Portfolio Asset Allocation</ChartCaption>
          <HistoricalAllocationChart data={data.historical_allocation} colours={colours} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'credit_exposure_bar':
      if (!data.credit_exposure) return null;
      return (
        <>
          <ChartCaption>Credit Grade Exposure</ChartCaption>
          <CreditExposureBarChart data={data.credit_exposure} colours={colours} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'fs_allocation_bar':
      return (
        <>
          <ChartCaption>Asset Allocation</ChartCaption>
          <AllocationBarChart allocation={data.allocation} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'fs_credit_pie':
      if (!data.credit_exposure) return null;
      return (
        <>
          <ChartCaption>Credit Grade Exposure</ChartCaption>
          <FSCreditExposureChart creditExposure={data.credit_exposure} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'fs_performance_line':
      return (
        <>
          <ChartCaption>Performance Since Inception</ChartCaption>
          <FSPerformanceChart timeSeries={data.time_series} portfolioName={data.portfolio_name} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'waterfall_chart':
      if (!data.contribution_analysis) return null;
      return (
        <>
          <ChartCaption>Contribution Waterfall</ChartCaption>
          <WaterfallChart data={data.contribution_analysis.three_month} colours={colours} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'sector_exposure_chart':
      return (
        <>
          <ChartCaption>Sector Exposure vs SAA</ChartCaption>
          <SectorExposureChart data={data.allocation_vs_saa} colours={colours} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'risk_return_scatter':
      return (
        <>
          <ChartCaption>Risk vs Return</ChartCaption>
          <RiskReturnScatterChart performance={data.performance} volatilityData={data.volatility_data} colours={colours} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'drawdown_chart':
      return (
        <>
          <ChartCaption>Drawdown from Peak</ChartCaption>
          <DrawdownChart data={data.time_series} colours={colours} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'growth_defensive_chart':
      return (
        <>
          <ChartCaption>Growth vs Defensive</ChartCaption>
          <GrowthDefensiveChart growthPct={data.neutral_growth_pct} allocation={data.allocation} colours={colours} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'regional_lookthrough_chart':
      if (!data.regional_lookthrough) return null;
      return (
        <>
          <ChartCaption>International Equity Region Exposure vs. Benchmark</ChartCaption>
          <RegionalLookthroughChart data={data.regional_lookthrough} colours={colours} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'sector_lookthrough_chart':
      if (!data.sector_lookthrough) return null;
      return (
        <>
          <ChartCaption>Australian Equities Sector Exposure vs. Benchmark</ChartCaption>
          <SectorLookthroughChart data={data.sector_lookthrough} colours={colours} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    case 'global_sector_lookthrough_chart':
      if (!data.global_sector_lookthrough) return null;
      return (
        <>
          <ChartCaption>International Equities Sector Exposure vs. Benchmark</ChartCaption>
          <SectorLookthroughChart data={data.global_sector_lookthrough} colours={colours} ref={(el) => { dynRefs[blockId] = el; }} />
        </>
      );
    default:
      return null;
  }
};

// ── Asset class merging & display order for holdings grouping ──
/** Merge "EM Equities" into "International Equities" */
const normaliseAssetClass = (ac) =>
  ac === 'EM Equities' ? 'International Equities' : ac;

const ASSET_CLASS_ORDER = [
  'Australian Equities', 'International Equities',
  'Real Estate', 'Infrastructure', 'Cash',
  'Government Bonds', 'Bonds', 'Corporate Debt', 'Credit',
];

const groupHoldingsByAssetClass = (holdings) => {
  const groups = {};
  holdings.forEach(h => {
    const assetClass = normaliseAssetClass(h.asset_class || 'Other');
    if (!groups[assetClass]) {
      groups[assetClass] = { name: assetClass, funds: [], total: 0 };
    }
    groups[assetClass].funds.push(h);
    groups[assetClass].total += h.weight;
  });
  return Object.values(groups).sort((a, b) => {
    const idxA = ASSET_CLASS_ORDER.indexOf(a.name);
    const idxB = ASSET_CLASS_ORDER.indexOf(b.name);
    return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
  });
};

const HoldingsByClassBlock = ({ data, editProps }) => {
  const holdings = data.holdings || [];
  if (holdings.length === 0) return <p style={{ color: '#999', fontStyle: 'italic' }}>No holdings data available</p>;
  const groups = groupHoldingsByAssetClass(holdings);
  const secondaryStyle = brandedHeaderStyle(data, 'secondary');
  return (
    <>
      <EditableHeading blockId="holdings_by_class" defaultText="PORTFOLIO HOLDINGS" data={data} editProps={editProps} />
      <table className="data-table holdings-table">
        <colgroup>
          <col style={{ width: '22%' }} />
          <col style={{ width: '60%' }} />
          <col style={{ width: '18%' }} />
        </colgroup>
        <thead style={secondaryStyle}>
          <tr>
            <th>Asset Class</th>
            <th>Fund</th>
            <th className="right">Weight</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group, gIdx) => (
            <React.Fragment key={gIdx}>
              {group.funds.map((fund, fIdx) => (
                <tr key={fIdx} className={fIdx === group.funds.length - 1 ? 'group-last-row' : ''}>
                  {fIdx === 0 && (
                    <td className={`asset-class-cell${gIdx === groups.length - 1 ? ' last-group' : ''}`} rowSpan={group.funds.length}>{group.name}</td>
                  )}
                  <td>{fund.name}</td>
                  <td className="right">{fund.weight.toFixed(1)}%</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
};

const Top10HoldingsBlock = ({ data, editProps }) => {
  const topHoldings = data.top_10_lookthrough || [];
  if (topHoldings.length === 0) return <p style={{ color: '#999', fontStyle: 'italic' }}>No top 10 holdings data available</p>;

  // Split into two columns of 5
  const leftCol = topHoldings.slice(0, 5);
  const rightCol = topHoldings.slice(5, 10);
  const secondaryStyle = brandedHeaderStyle(data, 'secondary');

  const renderColumn = (items, startIdx) => (
    <table className="data-table top10-table">
      <thead style={secondaryStyle}>
        <tr>
          <th></th>
          <th>Company</th>
          <th>Country</th>
          <th className="right">Weight</th>
        </tr>
      </thead>
      <tbody>
        {items.map((holding, idx) => (
          <tr key={idx}>
            <td style={{ color: '#6c757d' }}>{startIdx + idx + 1}</td>
            <td>{holding.name}</td>
            <td>{holding.country || holding.asset_class || '-'}</td>
            <td className="right">{holding.weight.toFixed(1)}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      <EditableHeading blockId="top10_holdings" defaultText="TOP 10 STOCK HOLDINGS" data={data} editProps={editProps} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        {renderColumn(leftCol, 0)}
        {rightCol.length > 0 && renderColumn(rightCol, 5)}
      </div>
    </>
  );
};

// ── Contribution analysis table block ───────────────────────────
const ContributionTableBlock = ({ data, editProps }) => {
  const ca = data.contribution_analysis;
  if (!ca || !ca.three_month) return null;

  const has12m = ca.twelve_month != null;
  const secondaryStyle = brandedHeaderStyle(data, 'secondary');
  const primaryStyle = brandedHeaderStyle(data, 'primary');
  const { headerPrimary } = getTableColours(data);

  const formatContrib = (val) => {
    if (val === null || val === undefined) return '\u2014';
    const sign = val > 0 ? '+' : '';
    return `${sign}${val.toFixed(2)}%`;
  };

  const colorClass = (val) => {
    if (val === null || val === undefined) return '';
    return val >= 0 ? 'contribution-positive' : 'contribution-negative';
  };

  const renderRows = (items) =>
    items.map((item, i) => (
      <tr key={i}>
        <td className="contribution-name">{item.asset_name}</td>
        <td className={`center ${colorClass(item.contribution_pct)}`}>{formatContrib(item.contribution_pct)}</td>
      </tr>
    ));

  const renderPeriodTable = (period, label) => (
    <div style={{ flex: 1 }}>
      <h3 className="contribution-period-label" style={{ color: headerPrimary }}>{label}</h3>
      <table className="data-table contribution-table">
        <thead style={primaryStyle}>
          <tr>
            <th>Asset Name</th>
            <th className="center">Contribution</th>
          </tr>
        </thead>
        <tbody>
          <tr className="contribution-sub-header"><td colSpan={2} style={secondaryStyle}>Top 5</td></tr>
          {renderRows(period.contributors)}
          <tr className="contribution-sub-header"><td colSpan={2} style={secondaryStyle}>Bottom 5</td></tr>
          {renderRows(period.detractors)}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <EditableHeading blockId="contribution_table" defaultText="CONTRIBUTION TO RETURN" data={data} editProps={editProps} />
      <div style={{ display: 'flex', gap: '12px' }}>
        {renderPeriodTable(ca.three_month, '3 Months')}
        {has12m && renderPeriodTable(ca.twelve_month, '12 Months')}
      </div>
    </>
  );
};

// ── Contribution Top/Bottom 3 table block ───────────────────────
const ContributionTable3Block = ({ data, editProps }) => {
  const ca = data.contribution_analysis;
  if (!ca || !ca.three_month) return null;

  const has12m = ca.twelve_month != null;
  const primaryStyle = brandedHeaderStyle(data, 'primary');
  const secondaryStyle = brandedHeaderStyle(data, 'secondary');

  const formatVal = (val) => {
    if (val === null || val === undefined) return '\u2014';
    return val.toFixed(2);
  };

  const renderSubTable = (items, title, isNegative) => (
    <div style={{ flex: 1 }}>
      <div className={`contrib3-header ${isNegative ? 'contrib3-header--negative' : ''}`} style={primaryStyle}>{title}</div>
      <table className="data-table contrib3-table">
        <thead style={secondaryStyle}>
          <tr>
            <th>Fund</th>
            <th className="right" style={{ whiteSpace: 'nowrap' }}>Contrib.</th>
          </tr>
        </thead>
        <tbody>
          {items.slice(0, 3).map((item, i) => (
            <tr key={i}>
              <td>{item.asset_name}</td>
              <td className="right">{formatVal(item.contribution_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <EditableHeading blockId="contribution_table_3" defaultText="PORTFOLIO UNDERLYING MANAGER CONTRIBUTIONS" data={data} editProps={editProps} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        {renderSubTable(ca.three_month.contributors, 'TOP 3 CONTRIBUTIONS 3 MONTH', false)}
        {renderSubTable(ca.three_month.detractors, 'BOTTOM 3 CONTRIBUTIONS 3 MONTH', true)}
        {has12m && renderSubTable(ca.twelve_month.contributors, 'TOP 3 CONTRIBUTIONS 12 MONTH', false)}
        {has12m && renderSubTable(ca.twelve_month.detractors, 'BOTTOM 3 CONTRIBUTIONS 12 MONTH', true)}
      </div>
    </>
  );
};

// ── International Equity Top 10 table block ────────────────────────────
const GlobalEquityTop10Block = ({ data, editProps }) => {
  const holdings = data.global_top_10_lookthrough || [];
  if (holdings.length === 0) return <p style={{ color: '#999', fontStyle: 'italic' }}>No international equity data available</p>;
  const secondaryStyle = brandedHeaderStyle(data, 'secondary');

  return (
    <>
      <EditableHeading blockId="global_equity_top10" defaultText="TOP 10 INTERNATIONAL EQUITY EXPOSURE" data={data} editProps={editProps} />
      <table className="data-table equity-exposure-table">
        <thead style={secondaryStyle}>
          <tr>
            <th>Security</th>
            <th className="right">Weight</th>
          </tr>
        </thead>
        <tbody>
          {holdings.map((h, i) => (
            <tr key={i}>
              <td>{titleCaseSecurity(h.name)}</td>
              <td className="right">{h.weight.toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

// ── Domestic Equity Top 10 table block ──────────────────────────
const DomesticEquityTop10Block = ({ data, editProps }) => {
  const holdings = data.domestic_top_10_lookthrough || [];
  if (holdings.length === 0) return <p style={{ color: '#999', fontStyle: 'italic' }}>No domestic equity data available</p>;
  const secondaryStyle = brandedHeaderStyle(data, 'secondary');

  return (
    <>
      <EditableHeading blockId="domestic_equity_top10" defaultText="TOP 10 DOMESTIC EQUITY EXPOSURE" data={data} editProps={editProps} />
      <table className="data-table equity-exposure-table">
        <thead style={secondaryStyle}>
          <tr>
            <th>Security</th>
            <th className="right">Weight</th>
          </tr>
        </thead>
        <tbody>
          {holdings.map((h, i) => (
            <tr key={i}>
              <td>{titleCaseSecurity(h.name)}</td>
              <td className="right">{h.weight.toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

// ── Market Review template table block ──────────────────────────
const MarketReviewTableBlock = ({ data, editProps }) => {
  const sections = data.market_returns || [];
  if (sections.length === 0) return null;
  const fmt = v => (v == null ? '\u2014' : `${(v * 100).toFixed(1)}%`);
  const primaryStyle = brandedHeaderStyle(data, 'primary');
  const secondaryStyle = brandedHeaderStyle(data, 'secondary');
  return (
    <>
      <EditableHeading blockId="market_review_table" defaultText="MARKET REVIEW" data={data} editProps={editProps} />
      <table className="market-review-table" style={{ borderTopColor: getTableColours(data).headerPrimary }}>
        <thead>
          <tr>
            <th className="label-col" style={primaryStyle}>Asset Class/Index</th>
            <th style={primaryStyle}>3 Months</th>
            <th style={primaryStyle}>6 Months</th>
            <th style={primaryStyle}>1 Year</th>
          </tr>
        </thead>
        <tbody>
          {sections.map(sec => (
            <React.Fragment key={sec.section}>
              <tr className="market-review-section-row">
                <td colSpan={4} style={secondaryStyle}>{sec.section}</td>
              </tr>
              {sec.rows.map(r => (
                <tr key={r.ticker}>
                  <td className="label-col">{r.label}</td>
                  <td>{fmt(r.change_3m)}</td>
                  <td>{fmt(r.change_6m)}</td>
                  <td>{fmt(r.change_1y)}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
};

// ── Market Review Monthly template table block ─────────────────
const MarketReviewMonthlyTableBlock = ({ data, editProps }) => {
  const sections = data.market_returns_monthly || [];
  if (sections.length === 0) return null;
  const fmt = v => (v == null ? '\u2014' : `${(v * 100).toFixed(1)}%`);
  const primaryStyle = brandedHeaderStyle(data, 'primary');
  const secondaryStyle = brandedHeaderStyle(data, 'secondary');
  return (
    <>
      <EditableHeading blockId="market_review_monthly_table" defaultText="MARKET REVIEW" data={data} editProps={editProps} />
      <table className="market-review-table" style={{ borderTopColor: getTableColours(data).headerPrimary }}>
        <thead>
          <tr>
            <th className="label-col" style={primaryStyle}>Asset Class/Index</th>
            <th style={primaryStyle}>1 Month</th>
            <th style={primaryStyle}>3 Months</th>
            <th style={primaryStyle}>6 Months</th>
            <th style={primaryStyle}>1 Year</th>
          </tr>
        </thead>
        <tbody>
          {sections.map(sec => (
            <React.Fragment key={sec.section}>
              <tr className="market-review-section-row">
                <td colSpan={5} style={secondaryStyle}>{sec.section}</td>
              </tr>
              {sec.rows.map(r => (
                <tr key={r.ticker}>
                  <td className="label-col">{r.label}</td>
                  <td>{fmt(r.change_1m)}</td>
                  <td>{fmt(r.change_3m)}</td>
                  <td>{fmt(r.change_6m)}</td>
                  <td>{fmt(r.change_1y)}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
};

// ── Tactical Tilts table ────────────────────────────────────────
const TACTICAL_TILTS_ORDER = [
  'Australian Equities', 'International Equities',
  'Real Estate', 'Infrastructure',
  'Government Bonds', 'Corporate Debt', 'Cash',
];

const TACTICAL_TILTS_STRONG_THRESHOLD_ACS = new Set([
  'Australian Equities', 'International Equities',
  'Corporate Debt', 'Government Bonds',
]);

const getTiltPosition = (tilt, assetClass) => {
  const strongThreshold = TACTICAL_TILTS_STRONG_THRESHOLD_ACS.has(assetClass) ? 5 : 3;
  if (tilt > strongThreshold) return 'Strong Overweight';
  if (tilt >= 0.5) return 'Overweight';
  if (Math.abs(tilt) < 0.5) return 'Neutral';
  if (tilt <= -strongThreshold) return 'Strong Underweight';
  return 'Underweight';
};

const TILT_POSITION_COLOURS = {
  'Strong Overweight': '#337B91',
  'Overweight':        '#337B91',
  'Neutral':           '#C0C0C0',
  'Underweight':       '#8E6B8E',
  'Strong Underweight':'#8E6B8E',
};

const TacticalTiltsTableBlock = ({ data, editProps }) => {
  const tilts = data.active_tilts || [];
  if (tilts.length === 0) return null;

  // Merge EM Equities into International Equities
  const merged = [];
  tilts.forEach(d => {
    const name = d.asset_class === 'EM Equities' ? 'International Equities' : d.asset_class;
    const existing = merged.findIndex(m => m.asset_class === name);
    if (existing !== -1) {
      merged[existing] = {
        ...merged[existing],
        actual_weight: (merged[existing].actual_weight ?? 0) + (d.actual_weight ?? 0),
        saa_weight: (merged[existing].saa_weight ?? 0) + (d.saa_weight ?? 0),
        active_tilt: (merged[existing].active_tilt ?? 0) + (d.active_tilt ?? 0),
      };
    } else {
      merged.push({ ...d, asset_class: name });
    }
  });

  // Filter and sort to display order
  const rows = TACTICAL_TILTS_ORDER
    .map(ac => merged.find(m => m.asset_class === ac))
    .filter(Boolean)
    .map(row => ({
      ...row,
      position: getTiltPosition(row.active_tilt, row.asset_class),
    }));

  if (rows.length === 0) return null;

  return (
    <>
      <EditableHeading blockId="tactical_tilts_table" defaultText="TACTICAL TILTS*" data={data} editProps={editProps} />
      <table className="tactical-tilts-table">
        <tbody>
          {rows.map(row => {
            const bg = TILT_POSITION_COLOURS[row.position] || '#C0C0C0';
            return (
              <tr key={row.asset_class}>
                <td className="tactical-tilts-label">{row.asset_class}</td>
                <td className="tactical-tilts-bar" style={{ backgroundColor: bg, color: '#fff' }}>
                  {row.position}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="tactical-tilts-footnote">
        *Look through asset class exposure including Dynamic Plus Fund
      </p>
    </>
  );
};

// ── Asset Class Summary table ───────────────────────────────────
const AssetClassSummaryTable = ({ data, editProps }) => {
  const rows = data.allocation_vs_saa || [];
  if (rows.length === 0) return <p style={{ color: '#999', fontStyle: 'italic' }}>No allocation vs SAA data available</p>;
  const secondaryStyle = brandedHeaderStyle(data, 'secondary');
  const colours = resolveColours(data);
  return (
    <>
      <EditableHeading blockId="asset_class_summary_table" defaultText="ASSET CLASS SUMMARY" data={data} editProps={editProps} />
      <table className="data-table">
        <thead style={secondaryStyle}>
          <tr>
            <th style={{ textAlign: 'left' }}>Asset Class</th>
            <th>Weight</th>
            <th>SAA</th>
            <th>Active Tilt</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ textAlign: 'left' }}>{r.asset_class}</td>
              <td>{formatPercent(r.actual_weight)}</td>
              <td>{formatPercent(r.saa_weight)}</td>
              <td style={{ color: (r.active_tilt || 0) >= 0 ? colours.table_header_primary : colours.waterfall_negative }}>
                {r.active_tilt != null ? `${r.active_tilt >= 0 ? '+' : ''}${Number(r.active_tilt).toFixed(1)}%` : '\u2014'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

// ── Risk Metrics table ──────────────────────────────────────────
const RiskMetricsTable = ({ data, editProps }) => {
  const secondaryStyle = brandedHeaderStyle(data, 'secondary');
  const perf = data.performance || {};
  const vol = data.volatility_data || {};
  const rows = [
    { metric: 'Volatility (p.a.)', portfolio: vol.portfolio_vol ?? vol.volatility, peer: vol.peer_vol ?? vol.peer_volatility },
    { metric: 'Sharpe Ratio', portfolio: perf.sharpe_ratio ?? vol.sharpe_ratio, peer: null },
    { metric: 'Max Drawdown', portfolio: perf.max_drawdown ?? vol.max_drawdown, peer: null },
    { metric: 'Return (1Y)', portfolio: perf.return_1y, peer: perf.peer_return_1y },
    { metric: 'Return (3Y)', portfolio: perf.return_3y, peer: perf.peer_return_3y },
    { metric: 'Return (5Y)', portfolio: perf.return_5y, peer: perf.peer_return_5y },
  ];
  const fmt = (v, isRatio) => {
    if (v == null) return '\u2014';
    return isRatio ? Number(v).toFixed(2) : formatPercent(v);
  };
  return (
    <>
      <EditableHeading blockId="risk_metrics_table" defaultText="RISK METRICS" data={data} editProps={editProps} />
      <table className="data-table">
        <thead style={secondaryStyle}>
          <tr>
            <th style={{ textAlign: 'left' }}>Metric</th>
            <th>Portfolio</th>
            <th>Peer / Benchmark</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ textAlign: 'left' }}>{r.metric}</td>
              <td>{fmt(r.portfolio, r.metric.includes('Sharpe'))}</td>
              <td>{fmt(r.peer, r.metric.includes('Sharpe'))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

// ── Fund Comparison table ───────────────────────────────────────
const FundComparisonTable = ({ data, editProps }) => {
  const holdings = data.holdings || [];
  if (holdings.length === 0) return <p style={{ color: '#999', fontStyle: 'italic' }}>No holdings data available</p>;
  const secondaryStyle = brandedHeaderStyle(data, 'secondary');
  return (
    <>
      <EditableHeading blockId="fund_comparison_table" defaultText="FUND COMPARISON" data={data} editProps={editProps} />
      <table className="data-table">
        <thead style={secondaryStyle}>
          <tr>
            <th style={{ textAlign: 'left' }}>Fund</th>
            <th>Asset Class</th>
            <th>Weight</th>
            <th>Growth / Defensive</th>
          </tr>
        </thead>
        <tbody>
          {holdings.map((h, i) => (
            <tr key={i}>
              <td style={{ textAlign: 'left' }}>{h.name}</td>
              <td>{h.asset_class || '\u2014'}</td>
              <td>{formatPercent(h.weight)}</td>
              <td>{h.growth_defensive || '\u2014'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

// ── Quarterly Attribution table ─────────────────────────────────
const QuarterlyAttributionTable = ({ data, editProps }) => {
  const ca = data.contribution_analysis?.three_month;
  if (!ca) return <p style={{ color: '#999', fontStyle: 'italic' }}>No quarterly attribution data available</p>;
  const items = [
    ...(ca.contributors || []).map(c => ({ name: c.asset_name, contribution: c.contribution_pct })),
    ...(ca.detractors || []).map(d => ({ name: d.asset_name, contribution: d.contribution_pct })),
  ].sort((a, b) => b.contribution - a.contribution);
  const secondaryStyle = brandedHeaderStyle(data, 'secondary');
  const colours = resolveColours(data);
  return (
    <>
      <EditableHeading blockId="quarterly_attribution_table" defaultText="QUARTERLY ATTRIBUTION" data={data} editProps={editProps} />
      <table className="data-table">
        <thead style={secondaryStyle}>
          <tr>
            <th style={{ width: '40px' }}>Rank</th>
            <th style={{ textAlign: 'left' }}>Asset Class / Holding</th>
            <th>Contribution</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td style={{ textAlign: 'left' }}>{item.name}</td>
              <td style={{ color: item.contribution >= 0 ? colours.table_header_primary : colours.waterfall_negative }}>
                {item.contribution >= 0 ? '+' : ''}{Number(item.contribution).toFixed(2)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

// ── Portfolio Characteristics table ─────────────────────────────
const PortfolioCharacteristicsTable = ({ data, editProps }) => {
  const perf = data.performance || {};
  const vol = data.volatility_data || {};
  const holdingsCount = data.holdings ? data.holdings.length : null;
  const growthPct = data.neutral_growth_pct;
  const fmt = (v, isRatio) => {
    if (v == null) return '\u2014';
    return isRatio ? Number(v).toFixed(2) : formatPercent(v);
  };
  const rows = [
    { label: 'Number of Holdings', value: holdingsCount != null ? holdingsCount : '\u2014' },
    { label: 'Growth Exposure', value: growthPct != null ? `${growthPct}%` : '\u2014' },
    { label: 'Volatility (p.a.)', value: fmt(vol.portfolio_vol ?? vol.volatility) },
    { label: 'Sharpe Ratio', value: fmt(perf.sharpe_ratio ?? vol.sharpe_ratio, true) },
    { label: 'Max Drawdown', value: fmt(perf.max_drawdown ?? vol.max_drawdown) },
    { label: 'ICR', value: perf.icr != null ? formatPercent(perf.icr) : '\u2014' },
  ];
  return (
    <>
      <EditableHeading blockId="portfolio_characteristics_table" defaultText="PORTFOLIO CHARACTERISTICS" data={data} editProps={editProps} />
      <table className="data-table">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ textAlign: 'left', fontWeight: 600 }}>{r.label}</td>
              <td>{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

// ── Dynamic template table block ────────────────────────────────
const TemplateTableBlock = ({ blockId, data, editProps }) => {
  const customBlocks = data.content?.custom_blocks || {};
  const blockDef = customBlocks[blockId];
  if (!blockDef) return null;

  const { templateId } = blockDef;

  switch (templateId) {
    case 'perf_risk_table':
      return <PerfTableBlock data={data} editProps={editProps} />;
    case 'perf_risk_monthly_table':
      return <PerfTableMonthlyBlock data={data} editProps={editProps} />;
    case 'fy_returns_table':
      return <FYTableBlock data={data} editProps={editProps} />;
    case 'holdings_table':
      return <HoldingsBlock data={data} editProps={editProps} />;
    case 'fs_holdings_by_class':
      return <HoldingsByClassBlock data={data} editProps={editProps} />;
    case 'fs_top10_holdings':
      return <Top10HoldingsBlock data={data} editProps={editProps} />;
    case 'contribution_table':
      return <ContributionTableBlock data={data} editProps={editProps} />;
    case 'market_review_table':
      return <MarketReviewTableBlock data={data} editProps={editProps} />;
    case 'market_review_monthly_table':
      return <MarketReviewMonthlyTableBlock data={data} editProps={editProps} />;
    case 'tactical_tilts_table':
      return <TacticalTiltsTableBlock data={data} editProps={editProps} />;
    case 'asset_class_summary_table':
      return <AssetClassSummaryTable data={data} editProps={editProps} />;
    case 'risk_metrics_table':
      return <RiskMetricsTable data={data} editProps={editProps} />;
    case 'fund_comparison_table':
      return <FundComparisonTable data={data} editProps={editProps} />;
    case 'quarterly_attribution_table':
      return <QuarterlyAttributionTable data={data} editProps={editProps} />;
    case 'portfolio_characteristics_table':
      return <PortfolioCharacteristicsTable data={data} editProps={editProps} />;
    case 'contribution_table_3':
      return <ContributionTable3Block data={data} editProps={editProps} />;
    case 'global_equity_top10':
      return <GlobalEquityTop10Block data={data} editProps={editProps} />;
    case 'domestic_equity_top10':
      return <DomesticEquityTop10Block data={data} editProps={editProps} />;
    default:
      return null;
  }
};

// ── Main BlockRenderer ──────────────────────────────────────────
const BlockRenderer = ({ blockId, data, chartRefs, editProps }) => {
  switch (blockId) {
    case 'title_box':                     return <TitleBox data={data} />;
    case 'intro_text':                    return <IntroTextBlock data={data} editProps={editProps} />;
    case 'market_overview':               return <MarketOverviewBlock data={data} editProps={editProps} />;
    case 'performance_review':            return <PerformanceReviewBlock data={data} editProps={editProps} />;
    case 'perf_table':                    return <PerfTableBlock data={data} editProps={editProps} />;
    case 'fy_table':                      return <FYTableBlock data={data} editProps={editProps} />;
    case 'holdings_and_characteristics':  return <HoldingsBlock data={data} editProps={editProps} />;
    case 'charts_tilts_and_allocation':   return <ChartsTiltsBlock data={data} chartRefs={chartRefs} />;
    case 'allocation_donut_chart':        return <AllocationDonutBlock data={data} chartRefs={chartRefs} />;
    case 'historical_allocation_chart':   return <HistoricalAllocationBlock data={data} chartRefs={chartRefs} />;
    case 'corporate_debt_review':         return <CorporateDebtBlock data={data} editProps={editProps} />;
    case 'credit_exposure_chart':         return <CreditExposureBlock data={data} chartRefs={chartRefs} />;
    case 'government_bonds_review':       return <GovernmentBondsBlock data={data} editProps={editProps} />;
    case 'international_equities_review': return <InternationalEquitiesBlock data={data} editProps={editProps} />;
    case 'australian_equities_review':    return <AustralianEquitiesBlock data={data} editProps={editProps} />;
    case 'real_assets_review':            return <RealAssetsBlock data={data} editProps={editProps} />;
    case 'global_alpha_review':           return <GlobalAlphaBlock data={data} editProps={editProps} />;
    case 'outlook':                       return <OutlookBlock data={data} editProps={editProps} />;
    case 'sign_off':                      return <SignOffBlock />;
    default: {
      // Handle custom/template blocks
      const customBlocks = data.content?.custom_blocks || {};
      const blockDef = customBlocks[blockId];
      if (!blockDef) return null;

      // Managed disclaimer: a text block flagged `managedDisclaimer` (its stored
      // text is only a snapshot for older frontends) or a pure 'disclaimer' block.
      if (blockDef.managedDisclaimer || blockDef.type === 'disclaimer') {
        return (
          <ManagedDisclaimerBlock
            blockId={blockId}
            blockDef={blockDef}
            managed={data.managed_disclaimers}
            editMode={!!editProps?.editMode}
            onBlockDefChange={editProps?.onBlockDefChange}
          />
        );
      }
      if (blockDef.type === 'text') {
        return <DynamicTextBlock blockId={blockId} data={data} editProps={editProps} />;
      }
      if (blockDef.type === 'image') {
        return <DynamicImageBlock blockId={blockId} data={data} editProps={editProps} />;
      }
      if (blockDef.type === 'shape') {
        return <DynamicShapeBlock blockId={blockId} data={data} editProps={editProps} />;
      }
      if (blockDef.type === 'data' && blockDef.templateId) {
        const tpl = blockDef.templateId;
        if (['perf_risk_table', 'perf_risk_monthly_table', 'fy_returns_table', 'holdings_table', 'fs_holdings_by_class', 'fs_top10_holdings', 'contribution_table', 'market_review_table', 'market_review_monthly_table', 'tactical_tilts_table', 'asset_class_summary_table', 'risk_metrics_table', 'fund_comparison_table', 'quarterly_attribution_table', 'portfolio_characteristics_table', 'contribution_table_3', 'global_equity_top10', 'domestic_equity_top10'].includes(tpl)) {
          return <TemplateTableBlock blockId={blockId} data={data} editProps={editProps} />;
        }
        return <TemplateChartBlock blockId={blockId} data={data} chartRefs={chartRefs} />;
      }
      return null;
    }
  }
};

export default BlockRenderer;
